import type { Bounds, Camera, Size } from '@/types';
import { visibleWorldBounds } from '@/core/coordinates/coordinateSystem';

/**
 * Pure description of the adaptive grid for a camera. The renderer (canvas) only
 * draws what this returns, so the level selection and fades are unit-testable.
 *
 * Grid spacing follows a 1-2-5 progression in world units and is chosen so that
 * minor cells are never smaller than `minMinorPx` on screen. Each level fades in
 * as it grows, which avoids the "pop" when the spacing changes.
 */
export interface GridLevel {
  /** World units between lines. */
  readonly spacing: number;
  /** Screen pixels between lines at the current zoom. */
  readonly screenSpacing: number;
  /** 0..1 line opacity multiplier. */
  readonly alpha: number;
}

export interface GridModel {
  readonly minor: GridLevel;
  readonly major: GridLevel;
  /** World region to draw lines over. */
  readonly bounds: Bounds;
  /** Whether the world origin axes are inside or near the viewport. */
  readonly showOrigin: boolean;
}

const STEPS = [1, 2, 5] as const;
const MAJOR_EVERY = 5;

/** Smallest 1-2-5 spacing whose screen size is at least `minPx`. */
export const chooseSpacing = (zoom: number, minPx: number): number => {
  let magnitude = 10 ** Math.floor(Math.log10(minPx / zoom));
  for (let guard = 0; guard < 64; guard++) {
    for (const step of STEPS) {
      const spacing = step * magnitude;
      if (spacing * zoom >= minPx) return spacing;
    }
    magnitude *= 10;
  }
  return magnitude;
};

/** Smooth 0..1 ramp for `value` between `from` and `to`. */
export const smoothstep = (from: number, to: number, value: number): number => {
  const t = Math.min(1, Math.max(0, (value - from) / (to - from)));
  return t * t * (3 - 2 * t);
};

export interface GridOptions {
  readonly minMinorPx?: number;
  readonly fadeStartPx?: number;
  readonly fadeEndPx?: number;
}

export const computeGridModel = (camera: Camera, viewport: Size, options: GridOptions = {}): GridModel => {
  const minMinorPx = options.minMinorPx ?? 14;
  const fadeStart = options.fadeStartPx ?? minMinorPx;
  const fadeEnd = options.fadeEndPx ?? minMinorPx * 3;

  const minorSpacing = chooseSpacing(camera.zoom, minMinorPx);
  const minorPx = minorSpacing * camera.zoom;
  const majorSpacing = minorSpacing * MAJOR_EVERY;
  const majorPx = majorSpacing * camera.zoom;

  const bounds = visibleWorldBounds(camera, viewport);
  const margin = majorSpacing;
  const showOrigin =
    bounds.minX - margin <= 0 && bounds.maxX + margin >= 0 && bounds.minY - margin <= 0 && bounds.maxY + margin >= 0;

  return {
    minor: { spacing: minorSpacing, screenSpacing: minorPx, alpha: smoothstep(fadeStart, fadeEnd, minorPx) },
    major: { spacing: majorSpacing, screenSpacing: majorPx, alpha: 1 },
    bounds,
    showOrigin,
  };
};

/** First multiple of `spacing` at or after `from`. */
export const firstLine = (from: number, spacing: number): number => Math.ceil(from / spacing) * spacing;

/** Number of lines of `spacing` needed to cover `bounds` in one axis. */
export const lineCount = (min: number, max: number, spacing: number): number =>
  Math.max(0, Math.floor(max / spacing) - Math.ceil(min / spacing) + 1);
